import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, DatePicker } from "antd";
import { CalendarPlus } from "@phosphor-icons/react";
import {
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
  InboxModalErrorBanner,
} from "../AIPane/AIPaneStyled";

/**
 * Confirm-deadline modal (deadline calendaring) — the EMPTY-PICKER
 * case only.
 *
 * When a card carries a computed deadline, [Create event] confirms
 * in one click with no modal (Matthew: "one-click card"). This modal
 * appears only when the system couldn't read the letter date and
 * refused to guess a statutory deadline — the human supplies the
 * date, and the BE stores it as the event date.
 *
 * Confirms via `onConfirm("YYYY-MM-DD")`; parent owns the dispatch.
 */
const ConfirmDeadlineModal = ({
  open,
  cardTitle = "",
  onClose,
  onConfirm,
  busy = false,
}) => {
  const [pickedDate, setPickedDate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPickedDate(null);
    setError(null);
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (!pickedDate) {
      setError("Pick the deadline date from the letter.");
      return;
    }
    setError(null);
    onConfirm(pickedDate.format("YYYY-MM-DD"));
  }, [pickedDate, onConfirm]);

  // No disabledDate on purpose (Bugbot #304): mail is routinely opened
  // AFTER the letter's date, so a legitimate statutory deadline can
  // already be in the past when the human calendars it. The BE accepts
  // any parseable date; blocking the past here would make late-opened
  // mail impossible to calendar truthfully.
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Create event"
      cancelText="Cancel"
      okButtonProps={{ disabled: !pickedDate || busy, loading: busy }}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <CalendarPlus size={16} weight="bold" />
          Set the deadline date
        </span>
      }
      destroyOnClose
    >
      {cardTitle && (
        <InboxModalHint style={{ marginBottom: 12 }}>{cardTitle}</InboxModalHint>
      )}
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="deadline-confirm-date">
          Deadline date (from the letter's printed date + review window)
        </InboxModalLabel>
        <DatePicker
          id="deadline-confirm-date"
          format="YYYY-MM-DD"
          value={pickedDate}
          onChange={setPickedDate}
          style={{ width: "100%" }}
        />
        <InboxModalHint>
          The system couldn't read a date off this letter, so it won't
          guess a statutory deadline — check the document and enter it
          here. The calendar event lands on the Matter in Salesforce.
        </InboxModalHint>
      </InboxModalFieldGroup>
      {error && <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>}
    </Modal>
  );
};

ConfirmDeadlineModal.propTypes = {
  open: PropTypes.bool.isRequired,
  cardTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

export default ConfirmDeadlineModal;
